import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './HomeTabs.css';
import { useDrafts } from '../contexts/DraftContext';
import { useAuth } from '../lib/useAuth';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import DraftBody from './DraftBody';
import { usePublishedPosts, PublishedPost, isPostsTestModeEnabled } from '../hooks/usePublishedPosts';
import { ThreadView } from './ThreadView';
import { useSettings } from '../contexts/SettingsContext';

type HomeTabKey = 'inspiration' | 'recent' | 'posted' | 'random';

interface HomeTabsProps {
  onCreateNewDraft: () => void;
}

interface HomeNavButtonProps {
  active?: boolean;
  ariaLabel: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  iconClassName?: string;
}

const HomeNavButton: React.FC<HomeNavButtonProps> = ({
  active = false,
  ariaLabel,
  label,
  icon,
  onClick,
  iconClassName = 'tab-icon',
}) => (
  <button
    className={`tab-button${active ? ' active' : ''}`}
    onClick={onClick}
    aria-pressed={active}
    aria-label={ariaLabel}
  >
    <span className={iconClassName} aria-hidden="true">
      {icon}
    </span>
    <span className="tab-label">{label}</span>
  </button>
);

const HomeTabs: React.FC<HomeTabsProps> = ({ onCreateNewDraft }) => {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [activeTab, setActiveTab] = useState<HomeTabKey>(() => {
    try {
      // Auto-select Posted tab in test mode
      if (isPostsTestModeEnabled()) {
        return 'posted';
      }
      const stored = localStorage.getItem('lw:homeTab') as HomeTabKey | null;
      return stored === 'inspiration' || stored === 'recent' || stored === 'posted' || stored === 'random' ? stored : 'recent';
    } catch {
      return 'recent';
    }
  });
  const { savedDrafts, deleteDraft, createReplyOrQuoteDraft } = useDrafts();
  const { session } = useAuth();

  // Fetch published posts for the Posted tab
  const {
    threads: publishedThreads,
    loading: postsLoading,
    loadingMore: postsLoadingMore,
    hasMore: postsHasMore,
    error: postsError,
    loadMoreError: postsLoadMoreError,
    refetch: refetchPosts,
    loadMore: loadMorePosts,
    deletePost,
    testMode: postsTestMode,
  } = usePublishedPosts(session?.user?.id, {
    showHidden: settings?.showHiddenPosts ?? false,
  });
  const [isBetaUser, setIsBetaUser] = useState(false);
  const [randomIndex, setRandomIndex] = useState<number | null>(null);
  const [isRandomSpinning, setIsRandomSpinning] = useState(false);
  const prevActiveTabRef = useRef<HomeTabKey>(activeTab);
  const [recentViewMode, setRecentViewMode] = useState<'card' | 'list'>(() => {
    try {
      const stored = localStorage.getItem('lw:recentViewMode') as 'card' | 'list' | null;
      return stored === 'card' || stored === 'list' ? stored : 'card';
    } catch {
      return 'card';
    }
  });
  const [recentSearchQuery, setRecentSearchQuery] = useState('');

  useEffect(() => {
    let isMounted = true;
    const checkBeta = async () => {
      try {
        const userId = session?.user?.id;
        if (!userId) {
          if (isMounted) setIsBetaUser(false);
          return;
        }
        const { data } = await supabase
          .from('beta_users')
          .select('beta_status')
          .eq('user_id', userId)
          .single();
        if (isMounted) setIsBetaUser(!!data?.beta_status);
      } catch {
        if (isMounted) setIsBetaUser(false);
      }
    };
    checkBeta();
    return () => { isMounted = false; };
  }, [session]);

  // Ensure we never land on a hidden tab
  useEffect(() => {
    // If Inspiration hidden, move to Recent (default)
    if (!isBetaUser && activeTab === 'inspiration') {
      setActiveTab('recent');
    }
    // If Posted tab hidden (user logged out and not in test mode), move to Recent (default)
    if (!session?.user?.id && !postsTestMode && activeTab === 'posted') {
      setActiveTab('recent');
    }
  }, [isBetaUser, activeTab, session, postsTestMode]);

  // Persist tab preference
  useEffect(() => {
    try { localStorage.setItem('lw:homeTab', activeTab); } catch {}
  }, [activeTab]);

  // Persist view mode preference
  useEffect(() => {
    try { localStorage.setItem('lw:recentViewMode', recentViewMode); } catch {}
  }, [recentViewMode]);

  // No auto-create here; DraftContext handles first-time creation

  const handleDeleteDraft = (e: React.MouseEvent, draftId: string) => {
    e.stopPropagation();
    deleteDraft(draftId);
  };

  // Handle reply/quote - create new draft with linked post already attached
  const handleReplyOrQuote = (post: PublishedPost, mode: 'reply' | 'quote') => {
    const newDraftId = createReplyOrQuoteDraft(post, mode);
    navigate(`/n/${newDraftId}`);
  };

  const handleDeletePost = useCallback(async (post: PublishedPost) => {
    const confirmMessage = post.hidden
      ? 'Retry deleting this hidden post from the remaining platforms?'
      : 'Delete this post from all platforms?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const result = await deletePost(post.id);
      if (Object.keys(result.errors).length > 0) {
        const platforms = Object.entries(result.errors)
          .map(([platform, message]) => `${platform}: ${message}`)
          .join(', ');
        window.alert(`Post removed locally but some platforms failed: ${platforms}`);
      } else if (post.hidden && result.action === 'deleted') {
        window.alert('Hidden post deleted fully.');
      }
    } catch {
      window.alert('Failed to delete post. Please try again.');
    }
  }, [deletePost]);

  const filteredRecentDrafts = useMemo(() => {
    if (!savedDrafts || savedDrafts.length === 0) return [];
    const sorted = [...savedDrafts].sort((a, b) => (b.updated || 0) - (a.updated || 0));
    if (!recentSearchQuery.trim()) return sorted;
    const query = recentSearchQuery.toLowerCase();
    return sorted.filter(draft => 
      draft.plainText?.toLowerCase().includes(query)
    );
  }, [savedDrafts, recentSearchQuery]);

  const randomDraft = useMemo(() => {
    if (randomIndex === null) return null;
    return savedDrafts?.[randomIndex] || null;
  }, [randomIndex, savedDrafts]);

  const randomDraftPreview = useMemo(() => {
    const text = randomDraft?.plainText || '';
    return text.length > 160 ? text.slice(0, 160) + '…' : text;
  }, [randomDraft]);

  const pickRandomIndex = useCallback(() => {
    if (!savedDrafts || savedDrafts.length === 0) {
      setRandomIndex(null);
      return;
    }
    if (savedDrafts.length === 1) {
      setRandomIndex(0);
    } else {
      let next = Math.floor(Math.random() * savedDrafts.length);
      // Avoid repeating the same index consecutively when possible
      if (randomIndex !== null && next === randomIndex) {
        next = (next + 1) % savedDrafts.length;
      }
      setRandomIndex(next);
    }
    setIsRandomSpinning(true);
    window.setTimeout(() => setIsRandomSpinning(false), 600);
  }, [savedDrafts, randomIndex]);

  // Manage random draft index when switching to Random (only on tab transition)
  useEffect(() => {
    const prev = prevActiveTabRef.current;
    if (prev !== 'random' && activeTab === 'random') {
      // Just entered Random tab → pick one
      pickRandomIndex();
    }
    prevActiveTabRef.current = activeTab;
  }, [activeTab, pickRandomIndex]);

  // Keep random index valid if list shrinks; do NOT reshuffle on content reorders/updates
  useEffect(() => {
    if (!savedDrafts || savedDrafts.length === 0) {
      setRandomIndex(null);
      return;
    }
    if (randomIndex !== null && randomIndex >= savedDrafts.length) {
      // Clamp to last valid index
      setRandomIndex(savedDrafts.length - 1);
    }
  }, [savedDrafts, randomIndex]);

  return (
    <div className="home-tabs-container">
      <div className="home-tabs-content" aria-live="polite">
        {/* No start/welcome view. The Recent tab handles showing the latest draft. */}

        {isBetaUser && activeTab === 'inspiration' && (
          <div className="home-tab-panel">
            <p className="home-subheading">Spark ideas to start writing.</p>
            <button className="post-button" onClick={onCreateNewDraft}>New Note</button>
          </div>
        )}

        {activeTab === 'recent' && (
          <div className="home-tab-panel recent-tab-panel">
            <div className="recent-tab-header">
              <input
                type="text"
                className="recent-search-input"
                placeholder="Search drafts..."
                value={recentSearchQuery}
                onChange={(e) => setRecentSearchQuery(e.target.value)}
                aria-label="Search drafts"
              />
              <div className="recent-view-toggle">
                <button
                  className={`view-toggle-button ${recentViewMode === 'card' ? 'active' : ''}`}
                  onClick={() => setRecentViewMode('card')}
                  aria-label="Card view"
                  title="Card view"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7"></rect>
                    <rect x="14" y="3" width="7" height="7"></rect>
                    <rect x="14" y="14" width="7" height="7"></rect>
                    <rect x="3" y="14" width="7" height="7"></rect>
                  </svg>
                </button>
                <button
                  className={`view-toggle-button ${recentViewMode === 'list' ? 'active' : ''}`}
                  onClick={() => setRecentViewMode('list')}
                  aria-label="List view"
                  title="List view"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="8" y1="6" x2="21" y2="6"></line>
                    <line x1="8" y1="12" x2="21" y2="12"></line>
                    <line x1="8" y1="18" x2="21" y2="18"></line>
                    <line x1="3" y1="6" x2="3.01" y2="6"></line>
                    <line x1="3" y1="12" x2="3.01" y2="12"></line>
                    <line x1="3" y1="18" x2="3.01" y2="18"></line>
                  </svg>
                </button>
              </div>
            </div>
            {filteredRecentDrafts.length === 0 ? (
              <p className="home-subheading">
                {recentSearchQuery.trim() ? 'No drafts match your search.' : 'No drafts yet. Create your first note.'}
              </p>
            ) : recentViewMode === 'card' ? (
              <div className="recent-cards-container">
                {filteredRecentDrafts.map((draft) => {
                  const preview = draft.plainText || '';
                  const previewText = preview.length > 160 ? preview.slice(0, 160) + '…' : preview;
                  const hasLinkedPost = !!draft.linkedPostId;
                  const linkedMode = draft.linkedPostMode;
                  return (
                    <button
                      key={draft.id}
                      className="home-latest-card"
                      onClick={(e) => {
                        const target = e.target as HTMLElement;
                        if (target.closest('.recent-card-delete-button')) return;
                        navigate(`/n/${draft.id}`);
                      }}
                    >
                      {hasLinkedPost && (
                        <div className="recent-card-header">
                          <div className="recent-card-header-left">
                            <div className="draft-linked-indicator" title={linkedMode === 'reply' ? 'Reply draft' : 'Quote draft'}>
                              {linkedMode === 'reply' ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="9 14 4 9 9 4"></polyline>
                                  <path d="M20 20v-7a4 4 0 0 0-4-4H4"></path>
                                </svg>
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                                </svg>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      {previewText && previewText.trim() !== '' ? (
                        <DraftBody text={previewText} />
                      ) : (
                        <span style={{ opacity: 0.5 }}>Empty Draft</span>
                      )}
                      <div className="home-latest-meta-container">
                        <div className="home-latest-meta">
                          Last edited: {new Date(draft.updated || Date.now()).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </div>
                        <button
                          className="recent-card-delete-button"
                          onClick={(e) => handleDeleteDraft(e, draft.id)}
                          aria-label="Delete draft"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="recent-delete-icon"
                          >
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                        </button>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="recent-list-container">
                {filteredRecentDrafts.map((draft) => {
                  const preview = draft.plainText || 'Empty draft';
                  const previewText = preview.length > 50 ? preview.slice(0, 50) + '...' : preview;
                  const hasLinkedPost = !!draft.linkedPostId;
                  const linkedMode = draft.linkedPostMode;
                  return (
                    <div
                      key={draft.id}
                      className="recent-list-item"
                      onClick={(e) => {
                        const target = e.target as HTMLElement;
                        if (target.closest('.recent-list-delete-button')) return;
                        navigate(`/n/${draft.id}`);
                      }}
                    >
                      <div className="recent-list-content">
                        <div className="recent-list-preview-row">
                          {hasLinkedPost && (
                            <div className="draft-linked-indicator-list" title={linkedMode === 'reply' ? 'Reply draft' : 'Quote draft'}>
                              {linkedMode === 'reply' ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="9 14 4 9 9 4"></polyline>
                                  <path d="M20 20v-7a4 4 0 0 0-4-4H4"></path>
                                </svg>
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                                </svg>
                              )}
                            </div>
                          )}
                          <div className="recent-list-preview">{previewText}</div>
                        </div>
                        <div className="recent-list-timestamp">
                          Last edited: {new Date(draft.updated || Date.now()).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </div>
                      </div>
                      <button 
                        className="recent-list-delete-button"
                        onClick={(e) => handleDeleteDraft(e, draft.id)}
                        aria-label="Delete draft"
                      >
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="24" 
                          height="24" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                          className="recent-delete-icon"
                        >
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {(session?.user?.id || postsTestMode) && activeTab === 'posted' && (
          <div className="home-tab-panel posted-tab-panel">
            {postsLoading ? (
              <p className="home-subheading">Loading posts...</p>
            ) : postsError ? (
              <p className="home-subheading">Failed to load posts. <button className="link-button" onClick={refetchPosts}>Retry</button></p>
            ) : publishedThreads.length === 0 ? (
              <p className="home-subheading">No posts yet. Your published posts will appear here.</p>
            ) : (
              <div className="posted-cards-container">
                {publishedThreads.map((thread) => (
                  <ThreadView
                    key={thread.root.id}
                    thread={thread}
                    onReply={(post) => handleReplyOrQuote(post, 'reply')}
                    onQuote={(post) => handleReplyOrQuote(post, 'quote')}
                    onDelete={handleDeletePost}
                  />
                ))}
                {postsLoadMoreError && (
                  <p className="posted-load-more-error">
                    Couldn&apos;t load more posts. <button className="link-button" onClick={loadMorePosts}>Retry</button>
                  </p>
                )}
                {postsHasMore && (
                  <button
                    className="posted-load-more"
                    onClick={loadMorePosts}
                    disabled={postsLoadingMore}
                  >
                    {postsLoadingMore ? 'Loading…' : 'Load more'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'random' && (
          <div className="home-tab-panel">
            {randomDraft ? (
              <button className="home-latest-card" onClick={() => navigate(`/n/${randomDraft.id}`)}>
                {randomDraftPreview && randomDraftPreview.trim() !== '' ? (
                  <DraftBody text={randomDraftPreview} />
                ) : (
                  <span style={{ opacity: 0.5, textAlign: 'center', fontStyle: 'italic' }}>Empty Draft</span>
                )}
                <div className="home-latest-meta-container">
                  <div className="home-latest-meta">Last edited: {new Date((randomDraft?.updated) || Date.now()).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
                  <button
                    className="recent-card-delete-button"
                    onClick={(e) => handleDeleteDraft(e, randomDraft.id)}
                    aria-label="Delete draft"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="recent-delete-icon"
                    >
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                  </button>
                </div>
              </button>
            ) : (
              <p className="home-subheading">No drafts yet. Create your first note.</p>
            )}
          </div>
        )}
      </div>

      <nav className="home-tabs-bar" aria-label="Home views">
        <HomeNavButton
          active={activeTab === 'recent'}
          ariaLabel="Recent"
          label="Drafts"
          onClick={() => {
            if (activeTab === 'random') {
              // intentionally do nothing extra on leaving random
            }
            setActiveTab('recent');
          }}
          icon={(
            // clock.svg
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-clock"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
          )}
        />

        <HomeNavButton
          active={activeTab === 'random'}
          ariaLabel="Random"
          label="Random"
          onClick={() => {
            if (activeTab === 'random') {
              pickRandomIndex();
            } else {
              setActiveTab('random');
            }
          }}
          iconClassName={`tab-icon${isRandomSpinning ? ' spinning' : ''}`}
          icon={(
            // refresh-ccw.svg
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-refresh-ccw"><polyline points="1 4 1 10 7 10"></polyline><polyline points="23 20 23 14 17 14"></polyline><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path></svg>
          )}
        />

        {(session?.user?.id || postsTestMode) && (
          <HomeNavButton
            active={activeTab === 'posted'}
            ariaLabel="Posted"
            label="Posted"
            onClick={() => setActiveTab('posted')}
            icon={(
              // send.svg
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-send"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
            )}
          />
        )}

        {isBetaUser && (
          <HomeNavButton
            active={activeTab === 'inspiration'}
            onClick={() => setActiveTab('inspiration')}
            ariaLabel="Inspiration"
            label="Inspiration"
            icon={(
              // zap.svg
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-zap"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
            )}
          />
        )}

        <HomeNavButton
          onClick={onCreateNewDraft}
          ariaLabel="New Draft"
          label="New"
          icon={(
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-plus-square"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>
          )}
        />
      </nav>
    </div>
  );
};

export default HomeTabs;
